import type { UIMessage } from "ai";
import { decodeToonToJson, tryDecodeToonString } from "@/lib/ai/convert/toon";
import { VIZ_RESULT_EVENT, VIZ_TOOL_NAME } from "@/lib/ai/visualization/config";
import {
  buildFallbackTree,
  buildTableFallbackSubstitutionNote,
  summarizeVizFailureReason,
} from "@/lib/ai/visualization/engine/render-fallback";
import { summarizeVizFollowupContextFacts } from "@/lib/ai/visualization/narration/followup-context";
import { isRecord } from "@/lib/ai/visualization/shared/primitives";
import type {
  VizAuthoringError,
  VizAuthoringTrace,
  VizRenderDataPartPayload,
  VizRenderFailureSummary,
  VizRenderOutcome,
  VizRenderOutput,
  VizRenderToolResult,
} from "@/lib/ai/visualization/types";

function toNullableString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * table fallback で出力済みの「データは表で表示済み」補足を抽出する。
 *
 * @remarks
 * fallback の `output.summary` には reason に続けて substitution note が付く。
 * authoringError 経由で message を組み直す経路でも同じ note を維持し、
 * 親モデルが描画失敗だけ読んで再試行・謝罪しないようにする。
 */
function extractTableFallbackSubstitutionNote(
  summary: string | null | undefined,
): string | null {
  if (typeof summary !== "string") return null;
  for (const note of [
    buildTableFallbackSubstitutionNote("Japanese"),
    buildTableFallbackSubstitutionNote(),
  ]) {
    if (summary.includes(note)) {
      return note;
    }
  }
  return null;
}

function toFailureIssues(
  output: Pick<VizRenderOutput, "authoringTrace">,
): VizRenderFailureSummary["issues"] | undefined {
  // Compact tool results intentionally stay short for the parent model. This
  // cap belongs to the short-result contract, not to authoring/narrative prompt
  // shaping.
  const issues = output.authoringTrace?.decisionGeneration.issues
    ?.slice(0, 2)
    .map((issue) => ({
      path: issue.path.slice(0, 128),
      message: issue.message.slice(0, 256),
      phase: issue.phase,
    }));
  return issues && issues.length > 0 ? issues : undefined;
}

/** UI に送ってよい成功または検証済み table substitution かを判定する。 */
export function isRenderableVizResultPayload(
  payload: Pick<VizRenderDataPartPayload, "outcome" | "output">,
): boolean {
  return (
    payload.outcome === "success" ||
    (payload.outcome === "fallback" &&
      payload.output.spec?.type === "table" &&
      payload.output.datasetForRender != null)
  );
}

function normalizeVizRenderTransportValue(value: unknown): unknown {
  const decodedBySharedHelper = decodeToonIfString(value);
  if (decodedBySharedHelper !== value) {
    return decodedBySharedHelper;
  }
  if (typeof value !== "string") {
    return value;
  }

  try {
    const decoded = decodeToonToJson(value);
    if (isVizRenderToolResult(decoded) || isVizRenderOutput(decoded)) {
      return decoded;
    }
  } catch {
    // tool output としては plain string もありうるため、
    // viz contract に一致しない decode 失敗は未変換のまま返す。
  }

  return value;
}

/**
 * 値が TOON 文字列なら decode し、それ以外は入力値をそのまま返す。
 *
 * @remarks
 * transport 境界では plain object と TOON 文字列が混在するため、
 * read-side の入口で 1 度だけ軽い decode を試みる。
 */
export function decodeToonIfString(value: unknown): unknown {
  if (typeof value !== "string") return value;
  const decoded = tryDecodeToonString(value);
  return decoded !== undefined ? decoded : value;
}

/**
 * `viz_render` の outcome 値かを検証する。
 *
 * @remarks
 * render / sanitize / history scan が同じ enum 契約を読むため、
 * outcome 判定は 1 か所に集約する。
 */
function isVizRenderOutcome(value: unknown): value is VizRenderOutcome {
  return value === "success" || value === "fallback" || value === "error";
}

/**
 * 永続化済み full visualization payload を検証する。
 *
 * @remarks
 * read path は compact result と full output の 2 形だけを正規入力として扱い、
 * それ以外の曖昧な shape は受け入れない。
 */
function isVizRenderOutput(value: unknown): value is VizRenderOutput {
  return (
    isRecord(value) &&
    Boolean(value.tree) &&
    typeof value.datasetId === "string"
  );
}

/**
 * model-facing compact result を検証する。
 *
 * @remarks
 * tool part には compact result だけを残すのが正本なので、
 * read-side でもこの shape を最優先で扱う。
 */
function isVizRenderToolResult(value: unknown): value is VizRenderToolResult {
  return (
    isRecord(value) &&
    typeof value.datasetId === "string" &&
    isVizRenderOutcome(value.outcome) &&
    "followupSummary" in value &&
    "failure" in value
  );
}

/**
 * full visualization payload から失敗要約を組み立てる。
 *
 * @remarks
 * 失敗の正本は `VizRenderOutput` 側に寄せ、compact result や sanitize は
 * この helper を通して同じ summary を再利用する。
 */
export function buildVizRenderFailureSummary(args: {
  output: Pick<
    VizRenderOutput,
    "summary" | "authoringTrace" | "authoringError"
  >;
  outcome: VizRenderOutcome;
  reason?: string;
}): VizRenderFailureSummary | null {
  if (args.outcome === "success") {
    return null;
  }

  if (args.output.authoringError) {
    const issues = toFailureIssues(args.output);
    const attemptDetails = args.output.authoringError.attempts;
    const attempts = attemptDetails
      ? {
          attemptCount: attemptDetails.attemptCount,
          retryApplied: attemptDetails.retryApplied,
        }
      : undefined;
    const reasonSummary = summarizeVizFailureReason(
      args.output.authoringError.reason,
    );
    // Provider / SDK 由来の error.message は Process details と model input へ流さない。
    // 機械可読 reason と正規化済み issue だけを公開診断の正本にする。
    const baseMessage = reasonSummary;
    // table fallback では substitution note を残し、親モデルへ「表で表示済み」を伝える。
    const substitutionNote =
      args.outcome === "fallback"
        ? extractTableFallbackSubstitutionNote(args.output.summary)
        : null;
    const message = substitutionNote
      ? `${baseMessage} ${substitutionNote}`
      : baseMessage;
    return {
      stage: args.output.authoringError.stage,
      reason: args.output.authoringError.reason,
      timedOut: args.output.authoringError.timedOut,
      message,
      ...(attempts ? { attempts } : {}),
      ...(issues ? { issues } : {}),
    };
  }

  const fallbackReason = args.reason ?? "viz-render-failed";
  return {
    stage: "precondition",
    reason: fallbackReason,
    timedOut: false,
    message:
      toNullableString(args.output.summary) ??
      summarizeVizFailureReason(fallbackReason),
  };
}

/**
 * full visualization payload を親モデル向け compact result へ縮約する。
 *
 * @remarks
 * `followupSummary` は success のときだけ残し、failure では
 * 「可視化済み」と誤読されない shape を固定で返す。
 */
export function buildVizRenderToolResult(args: {
  output: VizRenderOutput;
  outcome: VizRenderOutcome;
  reason?: string;
}): VizRenderToolResult {
  const followupSummary =
    args.outcome === "success"
      ? // 永続化済み payload は current follow-up 契約を満たさないことがある。
        // compact 化で chat 全体を落とさず、検証済み context だけ summary へ使う。
        (summarizeVizFollowupContextFacts(args.output.followupContext) ??
        toNullableString(args.output.summary))
      : null;

  return {
    datasetId: args.output.datasetId,
    outcome: args.outcome,
    followupSummary,
    failure: buildVizRenderFailureSummary(args),
  };
}

/**
 * future turn の main-model input に残してよい `viz_render` result かを返す。
 *
 * @remarks
 * current turn では final failure を main LLM へ返してよいが、その失敗を次 turn 以降の
 * 履歴へ残すと「前回 viz が壊れていたので」といった stale self-reference が起きやすい。
 * success result だけを future-turn continuity 用に残し、failure 詳細は current turn の
 * tool result と内部ログだけへ閉じ込める。
 */
export function shouldKeepVizRenderToolResultForModelInput(
  result: Pick<VizRenderToolResult, "outcome">,
): boolean {
  return result.outcome === "success";
}

/**
 * fallback / error の compact result を派生するための canonical output を組み立てる。
 *
 * @remarks
 * executor の成功/失敗で shape を分岐させないため、非表示 fallback tree を含む
 * canonical shape を内部で維持する。失敗時にこの tree を UI へは送らない。
 */
export function buildVizFallbackOutput(args: {
  datasetId: string;
  message: string;
  authoringTrace?: VizAuthoringTrace | null;
  authoringError?: VizAuthoringError | null;
}): VizRenderOutput {
  return {
    tree: buildFallbackTree(),
    datasetId: args.datasetId,
    summary: args.message,
    authoringTrace: args.authoringTrace ?? null,
    authoringError: args.authoringError ?? null,
  };
}

/**
 * persisted payload から debug 専用情報を除外する。
 *
 * @remarks
 * trace と詳細 error は内部診断にだけ必要で、再表示や follow-up continuity の
 * 正本には含めない。一方で `datasetForRender` は描画実体そのものなので保持し、
 * client が source snapshot から元 dataset を引き直さないようにする。
 */
function stripVizRenderDebugOutput(output: VizRenderOutput): VizRenderOutput {
  const {
    authoringTrace: _authoringTrace,
    authoringError: _authoringError,
    ...next
  } = output;
  return next;
}

/**
 * `data-viz-result` 向けの保存 payload を作る。
 *
 * @remarks
 * server 側は full output を 1 回だけ組み立て、この helper で
 * persisted payload と compact result の両方へ派生させる。描画に使う dataset は
 * compact result ではなく persisted payload 側へ閉じ込め、client はその render-ready
 * dataset を再表示と export の正本として読む。
 */
export function buildVizRenderDataPayload(args: {
  toolCallId?: string;
  outcome: VizRenderOutcome;
  reason?: string;
  output: VizRenderOutput;
}): VizRenderDataPartPayload {
  return {
    toolCallId: args.toolCallId,
    outcome: args.outcome,
    reason: args.reason,
    output: stripVizRenderDebugOutput(args.output),
  };
}

/**
 * tool part output を canonical compact result へ正規化する。
 *
 * @remarks
 * read path が受け入れるのは compact result か full output のみだが、
 * transport 境界では同じ payload が TOON 文字列化されることがある。
 * 判定前に decode を 1 度だけ通し、sanitize / history / live capture が
 * 同じ入力契約を共有できるようにする。
 */
export function compactVizRenderToolOutput(
  value: unknown,
): VizRenderToolResult | null {
  const normalized = normalizeVizRenderTransportValue(value);

  if (isVizRenderToolResult(normalized)) {
    return normalized;
  }
  if (!isVizRenderOutput(normalized)) {
    return null;
  }

  return buildVizRenderToolResult({
    output: normalized,
    outcome: normalized.authoringError ? "fallback" : "success",
  });
}

/**
 * visualization 履歴復元が依存する最小メッセージ契約。
 *
 * @remarks
 * `history` / plugin resolver で同じ scan helper を共有するため、
 * `UIMessage` 全体ではなく `parts` と任意の `id` だけへ依存を絞る。
 * `id` は signature と debug 用にだけ使うため、history 復元では optional に保つ。
 */
export type VizMessageLike = Pick<UIMessage, "parts"> &
  Partial<Pick<UIMessage, "id">>;

type VizMessagePart = UIMessage["parts"][number];

type VizToolPartState =
  | "input-streaming"
  | "input-available"
  | "output-available"
  | "output-error";

type VizToolUIPartLike = {
  toolCallId?: string;
  toolName?: string;
  type?: string;
  state?: VizToolPartState;
  input?: unknown;
  output?: unknown;
  errorText?: string;
};

/**
 * 1-pass scan 後の可視化関連 tool part。
 *
 * @remarks
 * downstream では raw part の再解釈を禁止し、この正規化済み shape を正本にする。
 * TOON decode と legacy payload の吸収も scan 時点で完了させ、read path の drift を防ぐ。
 */
export type ScannedVisualizationToolPart = {
  kind: "tool";
  messageId: string;
  partIndex: number;
  partKey: string;
  scanOrder: number;
  toolName: string;
  toolCallId: string | null;
  state: VizToolPartState | null;
  input: unknown;
  output: unknown;
  errorText: string | null;
  vizResultPayload: VizRenderDataPartPayload | null;
  vizRenderOutput: VizRenderOutput | null;
  vizRenderToolResult: VizRenderToolResult | null;
};

/**
 * 永続化済み `data-viz-result` part を scan 済みの read model に変換した結果。
 */
type ScannedVisualizationResultPart = {
  kind: "viz-result-data";
  messageId: string;
  partIndex: number;
  partKey: string;
  scanOrder: number;
  payload: VizRenderDataPartPayload;
};

/**
 * 可視化履歴復元で扱う part 正規化済み union。
 */
export type ScannedVisualizationPart =
  | ScannedVisualizationToolPart
  | ScannedVisualizationResultPart;

function toToolName(part: VizToolUIPartLike): string | null {
  if (typeof part.toolName === "string" && part.toolName.length > 0) {
    return part.toolName;
  }
  if (typeof part.type === "string" && part.type.startsWith("tool-")) {
    return part.type.slice("tool-".length);
  }
  return null;
}

function toToolPartState(value: unknown): VizToolPartState | null {
  return value === "input-streaming" ||
    value === "input-available" ||
    value === "output-available" ||
    value === "output-error"
    ? value
    : null;
}

/**
 * 可視化メッセージ内の transport 値を、client/runtime が読める形へそろえる。
 *
 * @remarks
 * API 境界では tool input/output が TOON 文字列のまま残ることがあるため、
 * scan・history 復元・live capture のすべてで同じ decode 規則を共有する。
 */
export function normalizeVisualizationTransportValue(value: unknown): unknown {
  return decodeToonIfString(value);
}

/**
 * tool part の output を描画復元用の値へ正規化する。
 *
 * @remarks
 * output 専用 helper を分けておくことで、呼び出し側が input/output の用途を
 * 誤解せずに済み、transport decode 規則だけを共通化できる。
 */
export function getRenderableToolOutput(value: unknown): unknown {
  return normalizeVisualizationTransportValue(value);
}

/**
 * tool part の output から `VizRenderToolResult` を復元する。
 *
 * @remarks
 * legacy full output しか残っていない履歴でも、dataset 解決や failure summary の
 * 分岐を共通化できるよう compact result へ正規化する。
 */
export function resolveVizRenderToolResult(
  value: unknown,
): VizRenderToolResult | null {
  return compactVizRenderToolOutput(getRenderableToolOutput(value));
}

/**
 * tool part の output から persisted full viz payload を復元する。
 *
 * @remarks
 * compact result しか残らない経路では null を返し、呼び出し側が
 * `data-viz-result` index へフォールバックできるようにする。
 */
export function resolveVizRenderOutput(value: unknown): VizRenderOutput | null {
  const output = getRenderableToolOutput(value);
  return isVizRenderOutput(output) ? output : null;
}

/**
 * message part から persisted visualization payload を取り出す。
 *
 * @remarks
 * 新形式の `data-viz-result` を優先し、tool result に full payload を残していた
 * 旧形式は read path のみで吸収する。
 */
function extractVizRenderDataPayloadFromPart(
  part: VizMessagePart,
): VizRenderDataPartPayload | null {
  if (!isRecord(part)) return null;

  if (part.type === VIZ_RESULT_EVENT) {
    const data = part.data;
    if (!isRecord(data) || !isVizRenderOutput(data.output)) {
      return null;
    }
    return {
      toolCallId:
        typeof data.toolCallId === "string" ? data.toolCallId : undefined,
      outcome:
        data.outcome === "success" ||
        data.outcome === "fallback" ||
        data.outcome === "error"
          ? data.outcome
          : "error",
      reason: typeof data.reason === "string" ? data.reason : undefined,
      output: data.output,
    };
  }

  const toolPart = part as VizToolUIPartLike;
  if (toToolName(toolPart) !== VIZ_TOOL_NAME) return null;
  if (toolPart.state !== "output-available") return null;
  const output = resolveVizRenderOutput(toolPart.output);
  if (!output) return null;

  return {
    toolCallId:
      typeof toolPart.toolCallId === "string" ? toolPart.toolCallId : undefined,
    outcome: output.authoringError ? "fallback" : "success",
    output,
  };
}

/**
 * 可視化に関係する message part を 1 回の scan で正規化する。
 *
 * @remarks
 * `history` と tool-part plugin が別々に part を再解釈すると
 * decode 規則や legacy 吸収条件が drift しやすいため、scan 規則をここへ集約する。
 */
export function scanVisualizationMessageParts(
  messages: readonly VizMessageLike[],
): ScannedVisualizationPart[] {
  const scannedParts: ScannedVisualizationPart[] = [];
  let scanOrder = 0;

  messages.forEach((message, messageIndex) => {
    const messageId =
      typeof message.id === "string" && message.id.length > 0
        ? message.id
        : `message-${messageIndex}`;
    const parts = Array.isArray(message.parts) ? message.parts : [];

    parts.forEach((rawPart, partIndex) => {
      scanOrder += 1;
      const partKey = `${messageId}:${partIndex}`;
      const vizResultPayload = extractVizRenderDataPayloadFromPart(rawPart);
      if (!isRecord(rawPart)) {
        if (!vizResultPayload) return;
        scannedParts.push({
          kind: "viz-result-data",
          messageId,
          partIndex,
          partKey,
          scanOrder,
          payload: vizResultPayload,
        });
        return;
      }

      const toolPart = rawPart as VizToolUIPartLike;
      const toolName = toToolName(toolPart);
      if (!toolName) {
        if (!vizResultPayload) return;
        scannedParts.push({
          kind: "viz-result-data",
          messageId,
          partIndex,
          partKey,
          scanOrder,
          payload: vizResultPayload,
        });
        return;
      }

      scannedParts.push({
        kind: "tool",
        messageId,
        partIndex,
        partKey,
        scanOrder,
        toolName,
        toolCallId:
          typeof toolPart.toolCallId === "string" &&
          toolPart.toolCallId.length > 0
            ? toolPart.toolCallId
            : null,
        state: toToolPartState(toolPart.state),
        input: normalizeVisualizationTransportValue(toolPart.input) ?? null,
        output: normalizeVisualizationTransportValue(toolPart.output) ?? null,
        errorText:
          typeof toolPart.errorText === "string" ? toolPart.errorText : null,
        vizResultPayload,
        vizRenderOutput: resolveVizRenderOutput(toolPart.output),
        vizRenderToolResult: resolveVizRenderToolResult(toolPart.output),
      });
    });
  });

  return scannedParts;
}
